import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { getViewedHotelTypeaheadEntries } from '@/components/TripActivity/utils'
import analytics from '@/shared-utils/analytics'
import { fetchViewedItems } from '@/shared-utils/fetchViewedItems'

import { useHotelTypeaheadLocationsFromEntries } from './useHotelTypeaheadLocationsFromEntries'

const MAX_VIEWED_HOTEL_SEARCHES = 3

type UseViewedSearchesFetchedParams = {
  cguid: string
  appName: string
  appVersion: string
  signedIn: boolean
  enabled: boolean
}

export function useViewedSearchesFetched({
  cguid,
  appName,
  appVersion,
  signedIn,
  enabled,
}: UseViewedSearchesFetchedParams) {
  const { data: viewedSelections = [], isFetched: isViewedFetched } = useQuery({
    queryKey: [cguid, appName, appVersion, signedIn, 'viewed-hotel-searches'],
    queryFn: async () => {
      try {
        return await fetchViewedItems({
          cguid,
          appName,
          appVersion,
          signedIn,
        })
      } catch (error) {
        analytics.logError({
          message: 'Error thrown from useQuery',
          error: JSON.stringify(error),
          service: 'fetchViewedItems',
        })
        return []
      }
    },
    staleTime: Infinity,
    enabled,
  })

  const viewedTypeaheadEntries = useMemo(
    () => getViewedHotelTypeaheadEntries(viewedSelections, MAX_VIEWED_HOTEL_SEARCHES),
    [viewedSelections],
  )

  return useHotelTypeaheadLocationsFromEntries({
    entries: viewedTypeaheadEntries,
    isSourceFetched: isViewedFetched,
  })
}
